/**
 * The page around the game: installing it, keeping it fullscreen, and keeping
 * a thumb on the screen from leaving it.
 */

/** Register the service worker (`public/sw.js`). Only in a built deploy: in development it would cache the dev server. */
export function registerServiceWorker(): void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((error: unknown) => {
      console.warn('[shell] service worker not registered:', error);
    });
  });
}

/** True when running as an installed app rather than in a browser tab. */
export function isInstalled(): boolean {
  return window.matchMedia('(display-mode: fullscreen), (display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/**
 * Keep the browser's back gesture from leaving the game.
 *
 * Steering with a thumb near the screen edge is exactly the gesture Android
 * and iOS read as "back", and one lost drive is enough to make a player stop
 * trusting the controls. So a guard entry sits on the history stack; going
 * back lands on it, `onBack` gets a chance to close an overlay, and the guard
 * is put back. Leaving on purpose still works: back twice in quick succession,
 * or the browser's own controls.
 */
export function guardBackNavigation(onBack: () => boolean): void {
  let lastBack = 0;
  const arm = () => history.pushState({ wsgGuard: true }, '', window.location.href);
  // Only once the player has touched the game: a guard pushed on load would
  // trap someone who opened the link by mistake.
  const start = () => {
    window.removeEventListener('pointerdown', start);
    if ((history.state as { wsgGuard?: boolean } | null)?.wsgGuard !== true) arm();
  };
  window.addEventListener('pointerdown', start);
  window.addEventListener('popstate', () => {
    const now = performance.now();
    const handled = onBack();
    if (!handled && now - lastBack < 600) {
      history.back(); // Back twice, quickly: really leave.
      return;
    }
    lastBack = now;
    arm();
  });
}

/**
 * Go fullscreen on the first touch, in a browser tab on a touch device: the
 * address bar and the system bars take a third of a landscape phone screen.
 * An installed app is fullscreen already. (iPhone Safari has no fullscreen
 * API; installing it to the home screen is the way there.)
 */
export function fullscreenOnFirstTouch(): void {
  if (isInstalled() || !window.matchMedia('(pointer: coarse)').matches) return;
  const go = () => {
    window.removeEventListener('pointerup', go);
    const root = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => void };
    if (document.fullscreenElement !== null) return;
    if (root.requestFullscreen !== undefined) root.requestFullscreen({ navigationUI: 'hide' }).catch(() => {});
    else root.webkitRequestFullscreen?.();
  };
  window.addEventListener('pointerup', go);
}

/**
 * Keep the screen awake while the game is on screen.
 *
 * A phone dims its screen, then locks it, when it has not been touched for a
 * while — and much of playing is not touching: a parachute drop, a straight
 * stretch of motorway, a look around. The Screen Wake Lock API holds it on.
 * The lock is released whenever the page is hidden, so it is taken again each
 * time the game comes back; where the API is missing or refused (low battery,
 * an older browser) the phone simply dims as before.
 */
export function keepScreenAwake(): void {
  type Sentinel = { released: boolean; release(): Promise<void>; addEventListener(type: 'release', fn: () => void): void };
  const wakeLock = (navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<Sentinel> } }).wakeLock;
  if (wakeLock === undefined) return;
  let held: Sentinel | undefined;
  let asking = false;
  const take = () => {
    if (asking || document.visibilityState !== 'visible' || (held !== undefined && !held.released)) return;
    asking = true;
    wakeLock.request('screen').then((sentinel) => {
      held = sentinel;
      sentinel.addEventListener('release', () => { held = undefined; });
    }).catch(() => {
      // Refused — power saving, or no user gesture yet: the next touch tries again.
    }).finally(() => { asking = false; });
  };
  take();
  document.addEventListener('visibilitychange', take);
  // Some browsers want a user gesture first; any touch or click retakes it.
  window.addEventListener('pointerup', take);
}
